import React, { useState, useEffect } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import api from '@services/api';
import { LoadingSpinner, Switch } from '@components/common';
import './ImagenGenerator.scss';

const ImagenGenerator = ({
  entity = null,
  onImageGenerated = null,
  compact = false,
  className = ""
}) => {
  const { t } = useLanguage();
  const notify = useNotification();

  // Form state
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [description, setDescription] = useState('');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [numberOfImages, setNumberOfImages] = useState(1);
  const [safetyLevel, setSafetyLevel] = useState('block_some');
  const [autoAttach, setAutoAttach] = useState(true);
  const [enhancePrompt, setEnhancePrompt] = useState(true);

  // UI state
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/imagen/config');
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading Imagen config:', error);
      notify.error(t('imagen.errors.configLoadFailed'));
    }
  };

  const handleGenerate = async () => {
    // Validate prompt requirements
    if (entity.model_type && entity.id && !enhancePrompt && !prompt.trim()) {
      notify.error(t('imagen.errors.promptRequiredWhenEnhanceDisabled'));
      return;
    }

    setLoading(true);
    try {
      let response;

      if (entity.model_type && entity.id) {
        // Generate for specific entity
        response = await api.post('/imagen/generate-for-entity', {
          entity_type: entity.model_type,
          entity_id: entity.id,
          prompt: prompt.trim(),
          negative_prompt: negativePrompt.trim() || undefined,
          aspect_ratio: aspectRatio,
          safety_filter_level: safetyLevel,
          number_of_images: numberOfImages,
          description: description.trim() || undefined,
          auto_attach: autoAttach,
          enhance_prompt: enhancePrompt
        });

        if (response.data.success) {
          notify.success(
            t('imagen.success.entityGenerated', {
              count: response.data.images_generated,
              entity: entity.model_type
            })
          );

          if (onImageGenerated) {
            onImageGenerated({
              success: true,
              attachments: response.data.attachments_created,
              entityType: entity.model_type,
              entityId: entity.id
            });
          }
        }
      } else {
        // General image generation
        response = await api.post('/imagen/generate', {
          prompt: prompt.trim(),
          negative_prompt: negativePrompt.trim() || undefined,
          aspect_ratio: aspectRatio,
          safety_filter_level: safetyLevel,
          number_of_images: numberOfImages,
          entity_type: entity.map_type || null,
          entity_id: entity.id || null,
          description: description.trim() || undefined
        });

        if (response.data.success) {
          setGeneratedImages(response.data.images);
          notify.success(
            t('imagen.success.generated', { count: response.data.total_images })
          );

          if (onImageGenerated) {
            onImageGenerated({
              success: true,
              images: response.data.images
            });
          }
        }
      }

    } catch (error) {
      console.error('Error generating image:', error);
      const errorMessage = error.response?.data?.detail || t('imagen.errors.generationFailed');
      notify.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const resetForm = () => {
    setPrompt('');
    setNegativePrompt('');
    setDescription('');
    setGeneratedImages([]);
  };

  if (!config) {
    return <LoadingSpinner />;
  }

  if (compact) {
    return (
      <div className={`imagen-generator imagen-generator--compact ${className}`}>
        <div className="imagen-generator__prompt-group">
          <textarea
            className="imagen-generator__prompt"
            placeholder={t('imagen.generator.promptPlaceholder')}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={2}
          />
          <button
            className={`imagen-generator__generate-btn imagen-generator__generate-btn--compact ${loading ? 'imagen-generator__generate-btn--loading' : ''}`}
            onClick={handleGenerate}
            disabled={loading || (entity.model_type && entity.id && !enhancePrompt && !prompt.trim())}
          >
            <div className="imagen-generator__btn-inner">
              {loading ? (
                <>
                  <div className="imagen-generator__spinner"></div>
                  <span>{t('imagen.generator.generating')}</span>
                </>
              ) : (
                <span>{t('imagen.generator.generate')}</span>
              )}
            </div>
          </button>
        </div>

        {/* Enhance Prompt Switch for compact mode */}
        {(entity.model_type && entity.id) && (
          <div className="imagen-generator__compact-switch">
            <Switch
              checked={enhancePrompt}
              onChange={setEnhancePrompt}
              label={t('imagen.generator.enhancePrompt')}
              size="small"
            />
          </div>
        )}

        {generatedImages.length > 0 && (
          <div className="imagen-generator__results">
            {generatedImages.map((image, index) => (
              <div key={index} className="imagen-generator__result-image">
                <img
                  src={`data:image/png;base64,${image.image_data}`}
                  alt={`Generated ${index + 1}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`imagen-generator ${className}`}>
      <div className="imagen-generator__header">
        <h3 className="imagen-generator__title">
          {entity.model_type ?
            t('imagen.generator.forEntity', { entity: entity.model_type }) :
            t('imagen.generator.title')
          }
        </h3>
      </div>

      <div className="imagen-generator__form">
        {/* Main Prompt */}
        <div className="imagen-generator__field">
          <label className="imagen-generator__label">
            {t('imagen.generator.prompt')} *
          </label>
          <textarea
            className="imagen-generator__prompt"
            placeholder={t('imagen.generator.promptPlaceholder')}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
          />
        </div>

        {/* Negative Prompt */}
        <div className="imagen-generator__field">
          <label className="imagen-generator__label">
            {t('imagen.generator.negativePrompt')}
          </label>
          <input
            type="text"
            className="imagen-generator__input"
            placeholder={t('imagen.generator.negativePromptPlaceholder')}
            value={negativePrompt}
            onChange={(e) => setNegativePrompt(e.target.value)}
          />
        </div>

        {/* Description for attachment */}
        {(entity.model_type && entity.id) && (
          <div className="imagen-generator__field">
            <label className="imagen-generator__label">
              {t('imagen.generator.description')}
            </label>
            <input
              type="text"
              className="imagen-generator__input"
              placeholder={t('imagen.generator.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        )}

        {/* Basic Settings */}
        <div className="imagen-generator__row">
          <div className="imagen-generator__field">
            <label className="imagen-generator__label">
              {t('imagen.generator.aspectRatio')}
            </label>
            <select
              className="imagen-generator__select"
              value={aspectRatio}
              onChange={(e) => setAspectRatio(e.target.value)}
            >
              {config.aspect_ratios.map(ratio => (
                <option key={ratio.value} value={ratio.value}>
                  {ratio.label}
                </option>
              ))}
            </select>
          </div>

          <div className="imagen-generator__field">
            <label className="imagen-generator__label">
              {t('imagen.generator.numberOfImages')}
            </label>
            <select
              className="imagen-generator__select"
              value={numberOfImages}
              onChange={(e) => setNumberOfImages(parseInt(e.target.value))}
            >
              {[1, 2, 3, 4].map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Advanced Settings Toggle */}
        <button
          type="button"
          className="imagen-generator__advanced-toggle"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? t('imagen.generator.hideAdvanced') : t('imagen.generator.showAdvanced')}
        </button>

        {showAdvanced && (
          <div className="imagen-generator__advanced">
            <div className="imagen-generator__field">
              <label className="imagen-generator__label">
                {t('imagen.generator.safetyLevel')}
              </label>
              <select
                className="imagen-generator__select"
                value={safetyLevel}
                onChange={(e) => setSafetyLevel(e.target.value)}
              >
                {config.safety_levels.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>

            {(entity.model_type && entity.id) && (
              <>
                <div className="imagen-generator__field">
                  <label className="imagen-generator__checkbox-label">
                    <input
                      type="checkbox"
                      checked={autoAttach}
                      onChange={(e) => setAutoAttach(e.target.checked)}
                    />
                    {t('imagen.generator.autoAttach')}
                  </label>
                </div>

                <div className="imagen-generator__field">
                  <label className="imagen-generator__checkbox-label">
                    <input
                      type="checkbox"
                      checked={enhancePrompt}
                      onChange={(e) => setEnhancePrompt(e.target.checked)}
                    />
                    {t('imagen.generator.enhancePrompt')}
                  </label>
                </div>
              </>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="imagen-generator__actions">
          <button
            className={`imagen-generator__generate-btn ${loading ? 'imagen-generator__generate-btn--loading' : ''}`}
            onClick={handleGenerate}
            disabled={loading || (entity.model_type && entity.id && !enhancePrompt && !prompt.trim())}
          >
            <div className="imagen-generator__btn-inner">
              {loading ? (
                <>
                  <div className="imagen-generator__spinner"></div>
                  <span>{t('imagen.generator.generating')}</span>
                </>
              ) : (
                <span>{t('imagen.generator.generate')}</span>
              )}
            </div>
          </button>

          {(generatedImages.length > 0 || prompt) && (
            <button
              className="imagen-generator__reset-btn"
              onClick={resetForm}
              disabled={loading}
            >
              {t('imagen.generator.reset')}
            </button>
          )}
        </div>

        {/* Info */}
        <div className="imagen-generator__info">
          <p>{t('imagen.generator.info')}</p>
          <ul className="imagen-generator__features">
            <li>{t('imagen.generator.feature1')}</li>
            <li>{t('imagen.generator.feature2')}</li>
            <li>{t('imagen.generator.feature3')}</li>
          </ul>
        </div>
      </div>

      {/* Generated Images */}
      {generatedImages.length > 0 && (
        <div className="imagen-generator__results">
          <h4 className="imagen-generator__results-title">
            {t('imagen.generator.generatedImages')}
          </h4>
          <div className="imagen-generator__images-grid">
            {generatedImages.map((image, index) => (
              <div key={index} className="imagen-generator__result-image">
                <img
                  src={`data:image/png;base64,${image.image_data}`}
                  alt={`Generated ${index + 1}`}
                />
                <div className="imagen-generator__image-info">
                  <span>{image.size.width}x{image.size.height}</span>
                  <span>{image.format}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagenGenerator;
